import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus, FileText, TrashSimple, Package, Buildings, CheckCircle, Warning, X,
  ArrowLeft, MapPin,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/dateUtils";
import SupplierQuickCreate from "@/components/SupplierQuickCreate";
import SupplierAutocomplete from "@/components/SupplierAutocomplete";
import CategorySelect from "@/components/CategorySelect";

const UNITS = ["adet", "kg", "ton", "m", "m²", "m³", "lt", "torba", "paket", "palet"];
const STATUS_LABELS = { aktif: "Aktif", tamamlandi: "Tamamlandı", iptal: "İptal" };
const STATUS_COLORS = {
  aktif: "bg-emerald-100 text-emerald-800 border-emerald-300",
  tamamlandi: "bg-slate-200 text-slate-700 border-slate-300",
  iptal: "bg-red-100 text-red-700 border-red-300",
};

const empty = {
  supplier_id: "", supplier_name: "",
  site_id: "", site_name: "",
  items: [{ material_name: "", unit: "adet", quantity: "", unit_price: "", vat_rate: 20, category: "" }],
  start_date: "", end_date: "",
  status: "aktif", notes: "",
};

export default function Contracts() {
  const { user } = useAuth();
  const { sites, activeSiteId, setActiveSiteId } = useSites();
  const { siteId: urlSiteId } = useParams();
  const isSiteScoped = !!urlSiteId;
  const currentSite = useMemo(
    () => (sites || []).find((s) => s.id === urlSiteId),
    [sites, urlSiteId],
  );
  const askConfirm = useConfirm();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState({ status: "all", category: "" });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Sözleşme dialog'undan hızlı firma oluşturma paneli
  const [supplierSheetOpen, setSupplierSheetOpen] = useState(false);
  const [supplierSaving, setSupplierSaving] = useState(false);
  // Modüle erişen tüm işlemleri yapabilir (ModuleGuard route'ta koruyor)
  const canManage = !!user;
  const isAdmin = !!user;

  const load = async () => {
    const params = {};
    // URL scoped: daima o şantiyenin sözleşmeleri
    if (isSiteScoped) params.site_id = urlSiteId;
    else if (activeSiteId && activeSiteId !== "all") params.site_id = activeSiteId;
    if (filter.status !== "all") params.status = filter.status;
    if (filter.category) params.category = filter.category;
    try {
      const { data } = await axios.get(`${API}/contracts`, { params });
      setRows(data);
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [activeSiteId, urlSiteId, filter]); // eslint-disable-line
  // Scoped modda üstteki genel şantiye seçicisini de senkronla
  useEffect(() => {
    if (isSiteScoped && activeSiteId !== urlSiteId) setActiveSiteId(urlSiteId);
  }, [isSiteScoped, urlSiteId]); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => {
    // URL scoped'da o şantiyeden başlar; değilse SiteContext'ten
    const preferSite = isSiteScoped
      ? sites.find(s => s.id === urlSiteId)
      : (activeSiteId && activeSiteId !== "all" ? sites.find(s => s.id === activeSiteId) : null);
    setEditing(null);
    setForm({
      ...empty,
      site_id: preferSite?.id || "",
      site_name: preferSite?.name || "",
    });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    // Çok kalemli sözleşme ise items[] kullan; değilse tek kalem alanlarından bir item oluştur
    const items = (c.items && c.items.length > 0)
      ? c.items.map(it => ({
          material_name: it.material_name || "",
          unit: it.unit || "adet",
          quantity: it.quantity || "",
          unit_price: it.unit_price || "",
          vat_rate: it.vat_rate !== undefined ? it.vat_rate : 20,
          category: it.category || "",
        }))
      : [{
          material_name: c.material_name || "",
          unit: c.unit || "adet",
          quantity: c.total_quantity || "",
          unit_price: c.unit_price || "",
          vat_rate: c.vat_rate !== undefined ? c.vat_rate : 20,
          category: c.category || "",
        }];
    setForm({
      supplier_id: c.supplier_id, supplier_name: c.supplier_name,
      site_id: c.site_id, site_name: c.site_name,
      items,
      start_date: c.start_date || "", end_date: c.end_date || "",
      status: c.status, notes: c.notes || "",
    });
    setOpen(true);
  };

  // Kalem yönetimi
  const addItem = () => {
    setForm(f => ({
      ...f,
      items: [...f.items, { material_name: "", unit: "adet", quantity: "", unit_price: "", vat_rate: 20, category: "" }],
    }));
  };
  const removeItem = (idx) => {
    setForm(f => ({
      ...f,
      items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
    }));
  };
  const updateItem = (idx, key, value) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? { ...it, [key]: value } : it),
    }));
  };

  const save = async () => {
    if (!form.supplier_id || !form.site_id) {
      toast.error("Firma ve şantiye zorunlu");
      return;
    }
    const validItems = form.items.filter(it => it.material_name && it.material_name.trim() && parseFloat(it.quantity) > 0);
    if (validItems.length === 0) {
      toast.error("En az bir kalem eklemelisiniz (malzeme adı ve miktar zorunlu)");
      return;
    }
    // Backend ContractIn: tek kalem alanları BACKWARD-COMPAT için birinci kaleme göre doldurulur.
    const first = validItems[0];
    const payload = {
      supplier_id: form.supplier_id, supplier_name: form.supplier_name,
      site_id: form.site_id, site_name: form.site_name,
      material_name: first.material_name,
      unit: first.unit,
      total_quantity: parseFloat(first.quantity) || 0,
      unit_price: parseFloat(first.unit_price) || 0,
      vat_rate: parseFloat(first.vat_rate) || 0,
      items: validItems.map(it => ({
        material_name: it.material_name.trim(),
        unit: it.unit || "adet",
        quantity: parseFloat(it.quantity) || 0,
        unit_price: parseFloat(it.unit_price) || 0,
        vat_rate: parseFloat(it.vat_rate) || 0,
        category: it.category || "",
      })),
      start_date: form.start_date, end_date: form.end_date,
      status: form.status, notes: form.notes,
    };
    try {
      if (editing) await axios.put(`${API}/contracts/${editing.id}`, payload);
      else await axios.post(`${API}/contracts`, payload);
      toast.success(editing ? "Sözleşme güncellendi" : "Sözleşme oluşturuldu");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (c) => {
    const ok = await askConfirm({
      title: "Sözleşmeyi Sil",
      description: `${c.code} sözleşmesini silmek istiyor musunuz? Bu işlem geri alınamaz.`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/contracts/${c.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === rows.length && rows.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map(r => r.id)));
    }
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await askConfirm({
      title: "Toplu Sözleşme Silme",
      description: `${selectedIds.size} adet sözleşmeyi silmek istiyor musunuz? 10 saniye içinde geri alabilirsiniz.`,
      destructive: true,
      confirmText: `Evet, ${selectedIds.size} Sözleşmeyi Sil`,
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${API}/contracts/bulk-delete`, {
        ids: Array.from(selectedIds),
      });
      const deletedIds = data.ids || Array.from(selectedIds);
      await load();
      toast.success(`${data.deleted} sözleşme silindi`, {
        duration: 10000,
        action: {
          label: "Geri Al",
          onClick: async () => {
            try {
              const res = await axios.post(`${API}/contracts/undo-delete`, { ids: deletedIds });
              toast.success(`${res.data.restored} sözleşme geri alındı`);
              await load();
            } catch (err) { toast.error(formatErr(err)); }
          },
        },
      });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Dialog'daki "Yeni Firma Tanımla" akışı → SupplierQuickCreate paneli
  const saveSupplierFromDialog = async (payload) => {
    setSupplierSaving(true);
    try {
      const r = await axios.post(`${API}/suppliers`, payload);
      const created = r.data;
      setSuppliers(prev => {
        const exists = prev.find(s => s.id === created.id);
        return exists ? prev : [...prev, created].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
      });
      setForm(f => ({ ...f, supplier_id: created.id, supplier_name: created.name || "" }));
      toast.success(`"${created.name}" firmalar listesine eklendi ve seçildi`);
      setSupplierSheetOpen(false);
      return created;
    } catch (e) {
      toast.error(formatErr(e));
      return null;
    } finally { setSupplierSaving(false); }
  };
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < rows.length;

  const stats = useMemo(() => {
    const aktif = rows.filter(r => r.status === "aktif");
    return {
      total: rows.length,
      active: aktif.length,
      totalQty: aktif.reduce((s, r) => s + (r.total_qty ?? r.total_quantity ?? 0), 0),
      deliveredQty: aktif.reduce((s, r) => s + (r.delivered_qty || 0), 0),
    };
  }, [rows]);

  return (
    <Shell
      title="Sözleşmeler"
      actions={canManage && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-contract"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              <Plus size={16} className="mr-2" /> Yeni Sözleşme
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold font-heading">
                {editing ? `Sözleşme Düzenle — ${editing.code}` : "Yeni Sözleşme"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma</Label>
                <div className="mt-2 flex items-stretch gap-2">
                  <div className="flex-1">
                    <SupplierAutocomplete
                      value={form.supplier_id}
                      suppliers={suppliers}
                      onChange={(s) => setForm(f => ({
                        ...f,
                        supplier_id: s?.id || "",
                        supplier_name: s?.name || "",
                      }))}
                      placeholder="Firma adı yazın (Örn: Dem)…"
                      testId="ct-supplier"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSupplierSheetOpen(true)}
                    data-testid="ct-new-supplier"
                    className="rounded-none sm:rounded-sm border-[#95C11F] text-[#4E7A0F] hover:bg-[#95C11F] hover:text-white text-xs uppercase tracking-widest font-bold whitespace-nowrap"
                    title="Yeni firma tanımla — sözleşmede seçilen firmayı doğrudan burada oluştur"
                  >
                    <Plus size={14} className="mr-1" /> Yeni Firma
                  </Button>
                </div>
                <div className="mt-1 text-[10px] text-slate-400">
                  Adının ilk harflerini yazın — eşleşen firmalar aşağıda listelenir. Yoksa &quot;Yeni Firma&quot; ile hızlıca ekleyebilirsiniz.
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şantiye</Label>
                <Select
                  value={form.site_id}
                  onValueChange={(v) => {
                    const s = sites.find(x => x.id === v);
                    setForm(f => ({ ...f, site_id: v, site_name: s?.name || "" }));
                  }}
                >
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="ct-site">
                    <SelectValue placeholder="Şantiye seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.filter(s => s.status === "aktif").map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Kalemler ({form.items.length})</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="ct-add-item"
                          className="rounded-none sm:rounded-sm text-[10px] uppercase tracking-widest font-bold border-[#1B4776] text-[#1B4776] hover:bg-[#1B4776] hover:text-white h-7">
                    <Plus size={12} className="mr-1" /> Kalem Ekle
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={idx}
                         data-testid={`ct-item-row-${idx}`}
                         className="relative grid grid-cols-12 gap-2 items-start p-2 border border-slate-200 bg-slate-50 rounded-none sm:rounded-sm">
                      <div className="col-span-12 sm:col-span-4">
                        <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Malzeme</Label>
                        <Input value={it.material_name}
                               data-testid={`ct-item-name-${idx}`}
                               onChange={e => updateItem(idx, "material_name", e.target.value)}
                               placeholder="Örn: Çimento CEM II 42.5R"
                               className="mt-1 rounded-none sm:rounded-sm h-9 bg-white" />
                      </div>
                      <div className="col-span-12 sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kategori</Label>
                        <div className="mt-1">
                          <CategorySelect
                            value={it.category || ""}
                            onChange={(v) => updateItem(idx, "category", v)}
                            allowEmpty={true}
                            allOptionLabel="Kategori Yok"
                            placeholder="Seç"
                            testIdPrefix={`ct-item-category-${idx}`}
                            className="h-9 bg-white text-xs"
                          />
                        </div>
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Miktar</Label>
                        <Input type="number" step="0.01" value={it.quantity}
                               data-testid={`ct-item-qty-${idx}`}
                               onChange={e => updateItem(idx, "quantity", e.target.value)}
                               className="mt-1 rounded-none sm:rounded-sm h-9 bg-white" />
                      </div>
                      <div className="col-span-4 sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Birim</Label>
                        <Select value={it.unit} onValueChange={(v) => updateItem(idx, "unit", v)}>
                          <SelectTrigger className="mt-1 rounded-none sm:rounded-sm h-9 bg-white text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Birim Fiyat (₺)</Label>
                        <Input type="number" step="0.01" value={it.unit_price}
                               data-testid={`ct-item-price-${idx}`}
                               onChange={e => updateItem(idx, "unit_price", e.target.value)}
                               className="mt-1 rounded-none sm:rounded-sm h-9 bg-white" />
                      </div>
                      <div className="col-span-8 sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">KDV %</Label>
                        <Input type="number" step="1" value={it.vat_rate}
                               onChange={e => updateItem(idx, "vat_rate", e.target.value)}
                               className="mt-1 rounded-none sm:rounded-sm h-9 bg-white" />
                      </div>
                      {form.items.length > 1 && (
                        <button type="button"
                                data-testid={`ct-item-remove-${idx}`}
                                onClick={() => removeItem(idx)}
                                className="absolute top-1 right-1 w-7 h-7 flex items-center justify-center hover:bg-red-50 border border-slate-300 bg-white"
                                title="Bu kalemi sil">
                          <TrashSimple size={12} className="text-red-600" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {/* Toplam özet */}
                {form.items.some(it => parseFloat(it.quantity) > 0 && parseFloat(it.unit_price) > 0) && (
                  <div className="mt-3 p-2 bg-slate-100 border border-slate-200 text-xs font-mono flex justify-end gap-4">
                    <span>Ara Toplam: ₺{form.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="font-bold text-[#1B4776]">
                      KDV Dahil: ₺{form.items.reduce((s, it) => {
                        const line = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
                        return s + line * (1 + (parseFloat(it.vat_rate) || 0) / 100);
                      }, 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Durum</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
                    <SelectItem value="iptal">İptal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Başlangıç</Label>
                <Input type="date" value={form.start_date}
                       onChange={e => set("start_date", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Bitiş</Label>
                <Input type="date" value={form.end_date}
                       onChange={e => set("end_date", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Notlar</Label>
                <Textarea value={form.notes} onChange={e => set("notes", e.target.value)}
                          rows={2} className="mt-2 rounded-none sm:rounded-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-contract"
                      className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                {editing ? "Güncelle" : "Sözleşme Oluştur"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    >
      {/* Şantiye Scope Header (URL /sozlesmeler/:siteId) */}
      {isSiteScoped && (
        <div className="flex items-center justify-between gap-4 p-3 mb-3 bg-blue-50/50 border border-[#1B4776]/40 rounded-none sm:rounded-sm"
             data-testid="ct-site-scope-header">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/sozlesmeler"
                  className="p-1.5 rounded-full bg-white border border-[#1B4776]/40 text-[#1B4776] hover:bg-[#1B4776]/10 transition"
                  data-testid="ct-back-to-index">
              <ArrowLeft size={14} weight="bold" />
            </Link>
            <MapPin size={18} weight="duotone" className="text-[#1B4776] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#1B4776] font-bold">
                Aktif Şantiye
              </div>
              <div className="text-sm font-bold text-[#0F172A] truncate">
                <span className="font-mono">{currentSite?.code || urlSiteId}</span>
                {currentSite?.name && <span className="ml-2 text-slate-700">— {currentSite.name}</span>}
              </div>
            </div>
          </div>
          <Link to="/sozlesmeler"
                className="text-xs text-[#1B4776] hover:underline shrink-0 font-semibold"
                data-testid="ct-change-site">
            Şantiye Değiştir
          </Link>
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Toplam</div>
          <div className="text-2xl font-bold text-slate-900 font-heading">{stats.total}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Aktif</div>
          <div className="text-2xl font-bold text-emerald-700 font-heading">{stats.active}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Toplam Miktar</div>
          <div className="text-lg font-bold text-slate-900 font-heading">{stats.totalQty.toLocaleString("tr-TR")}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776]">Teslim Edilen</div>
          <div className="text-lg font-bold text-[#1B4776] font-heading">{stats.deliveredQty.toLocaleString("tr-TR")}</div>
        </Card>
      </div>

      {/* Filter strip */}
      <Card className="p-3 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Durum</Label>
          <Select value={filter.status} onValueChange={(v) => setFilter(f => ({ ...f, status: v }))}>
            <SelectTrigger className="mt-1 rounded-none sm:rounded-sm w-40" data-testid="ct-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="aktif">Aktif</SelectItem>
              <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
              <SelectItem value="iptal">İptal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Malzeme Kategorisi</Label>
          <div className="mt-1 w-48">
            <CategorySelect
              value={filter.category}
              onChange={(v) => setFilter(f => ({ ...f, category: v }))}
              allowEmpty={true}
              allOptionLabel="Tümü"
              allowCreate={false}
              testIdPrefix="ct-filter-category"
              className="w-full"
            />
          </div>
        </div>
        {filter.category && (
          <Button variant="ghost" size="sm"
                  onClick={() => setFilter(f => ({ ...f, category: "" }))}
                  data-testid="ct-filter-category-clear"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold text-slate-600 hover:text-red-600">
            <X size={12} className="mr-1" /> Kategori Filtresini Temizle
          </Button>
        )}
        {isAdmin && rows.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
              <Checkbox
                checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                onCheckedChange={toggleAll}
                data-testid="ct-bulk-select-all"
                aria-label="Tümünü seç"
              />
              Tümünü Seç
            </label>
          </div>
        )}
      </Card>

      {/* Toplu seçim bar */}
      {isAdmin && selectedIds.size > 0 && (
        <div
          data-testid="ct-bulk-selection-bar"
          className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 mb-4 border border-red-200 bg-red-50 rounded-none sm:rounded-sm"
        >
          <div className="text-sm text-red-900 font-medium">
            <span className="font-mono font-bold" data-testid="ct-bulk-selection-count">{selectedIds.size}</span> sözleşme seçildi
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={clearSelection}
              data-testid="ct-bulk-clear-btn"
              className="h-8 rounded-none sm:rounded-sm border-slate-300 text-slate-700 hover:bg-slate-100 text-xs"
            >
              <X size={14} className="mr-1" /> Seçimi Temizle
            </Button>
            <Button
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="ct-bulk-delete-btn"
              className="h-8 rounded-none sm:rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest"
            >
              <TrashSimple size={14} className="mr-1.5" />
              {bulkDeleting ? "Siliniyor..." : `Seçilenleri Sil (${selectedIds.size})`}
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows.map((c) => {
          const pct = c.progress_percent || 0;
          const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 75 ? "bg-[#95C11F]" : "bg-[#1B4776]";
          const isChecked = selectedIds.has(c.id);
          return (
            <Card key={c.id} data-testid={`contract-${c.id}`}
                  className={`p-5 rounded-none sm:rounded-sm shadow-sm hover:shadow-md transition-shadow border ${isChecked ? "border-red-400 bg-red-50/40" : "border-slate-200"}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  {isAdmin && (
                    <div className="pt-1">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleOne(c.id)}
                        data-testid={`ct-bulk-select-${c.id}`}
                        aria-label={`Seç: ${c.code}`}
                      />
                    </div>
                  )}
                  <div className="w-11 h-11 bg-[#1B4776] flex items-center justify-center flex-shrink-0">
                    <FileText size={22} weight="duotone" className="text-[#95C11F]" />
                  </div>
                  <div className="cursor-pointer" onClick={() => nav(`/sozlesmeler/detay/${c.id}`)} data-testid={`ct-open-${c.id}`}>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">{c.code}</div>
                    <div className="text-base font-bold text-slate-900 font-heading leading-tight hover:text-[#1B4776] transition-colors">{c.material_name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-1 border ${STATUS_COLORS[c.status]}`}>
                    {STATUS_LABELS[c.status] || c.status}
                  </span>
                  {(() => {
                    const cs = c.confirmation_status || "pending";
                    const map = {
                      pending: { text: "Onay Bekleniyor", cls: "bg-amber-50 border-amber-300 text-amber-800" },
                      confirmed: { text: "Onaylandı", cls: "bg-emerald-50 border-emerald-300 text-emerald-800" },
                      rejected: { text: "Düzeltme Talep Edildi", cls: "bg-rose-50 border-rose-300 text-rose-800" },
                    };
                    const badge = map[cs] || map.pending;
                    return (
                      <span data-testid={`ct-confirm-badge-${c.id}`}
                            className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-1 border ${badge.cls}`}
                            title={cs === "confirmed" ? `Onaylayan: ${c.confirmation_confirmed_by_name || "-"}` : ""}>
                        {badge.text}
                      </span>
                    );
                  })()}
                  {canManage && (
                    <button onClick={() => del(c)}
                            className="w-7 h-7 flex items-center justify-center hover:bg-red-50">
                      <TrashSimple size={13} className="text-red-600" />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-3">
                <div className="flex items-center gap-1.5"><Buildings size={13} className="text-slate-400" /> {c.supplier_name}</div>
                <div className="flex items-center gap-1.5"><Package size={13} className="text-slate-400" /> {c.site_name}</div>
                {(c.start_date || c.end_date) && (
                  <div className="col-span-2 text-slate-500 font-mono text-[11px]">
                    {fmtDate(c.start_date) || "?"} → {fmtDate(c.end_date) || "?"}
                  </div>
                )}
              </div>

              {/* Çok kalemli sözleşme — kalem listesi */}
              {Array.isArray(c.items) && c.items.length > 0 && (
                <div className="mb-3 border border-slate-200 bg-slate-50/70 rounded-none sm:rounded-sm">
                  <div className="px-2 py-1.5 border-b border-slate-200 text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center justify-between">
                    <span>Kalemler ({c.items.length})</span>
                    {c.total_amount_incl_vat > 0 && (
                      <span className="font-mono text-slate-700">Toplam ₺{Number(c.total_amount_incl_vat).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} (KDV Dahil)</span>
                    )}
                  </div>
                  <div className="divide-y divide-slate-200">
                    {(c.items_progress || c.items).map((it, i) => {
                      const itQty = Number(it.quantity || 0);
                      const itDel = Number(it.delivered_qty || 0);
                      const itPct = Number(it.progress_percent || (itQty > 0 ? (itDel / itQty) * 100 : 0));
                      const itBar = itPct >= 100 ? "bg-emerald-500" : itPct >= 75 ? "bg-[#95C11F]" : "bg-[#1B4776]";
                      return (
                        <div key={i} className="px-2 py-2" data-testid={`ct-item-${c.id}-${i}`}>
                          <div className="flex items-start justify-between gap-2 text-[11px]">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <div className="font-bold text-slate-800 truncate">{it.material_name}</div>
                                {it.category && (
                                  <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold bg-[#F7FBE9] text-[#1B4776] border border-[#95C11F]/50">
                                    {it.category}
                                  </span>
                                )}
                              </div>
                              <div className="text-slate-500 font-mono mt-0.5">
                                {itQty.toLocaleString("tr-TR")} {it.unit}
                                {it.unit_price > 0 && ` × ₺${Number(it.unit_price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`}
                                {it.vat_rate > 0 && ` · KDV %${it.vat_rate}`}
                              </div>
                            </div>
                            <div className="shrink-0 text-right font-mono text-[11px]">
                              <div className={itPct >= 100 ? "text-emerald-700 font-bold" : "text-slate-700"}>
                                {itDel.toLocaleString("tr-TR")} / {itQty.toLocaleString("tr-TR")}
                              </div>
                              <div className={`text-[10px] ${itPct >= 100 ? "text-emerald-600" : "text-slate-500"}`}>%{itPct.toFixed(1)}</div>
                            </div>
                          </div>
                          <div className="mt-1 h-1.5 bg-slate-100 border border-slate-200">
                            <div className={`h-full ${itBar} transition-all`} style={{ width: `${Math.min(100, itPct)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* İlerleme Bar (toplam) */}
              <div>
                <div className="flex justify-between items-baseline mb-1">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Genel İlerleme</div>
                  <div className="text-xs font-mono text-slate-700">
                    <strong>{(c.delivered_qty || 0).toLocaleString("tr-TR")}</strong>
                    <span className="text-slate-400"> / {(c.total_qty ?? c.total_quantity ?? 0).toLocaleString("tr-TR")} {c.unit}</span>
                    <span className={`ml-2 font-bold ${pct >= 100 ? "text-emerald-700" : "text-[#1B4776]"}`}>
                      %{pct.toFixed(1)}
                    </span>
                  </div>
                </div>
                <div className="w-full h-3 bg-slate-100 border border-slate-200">
                  <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <div className="mt-1 text-[10px] text-slate-500">
                  Kalan: <strong className="font-mono">{(c.remaining_qty || 0).toLocaleString("tr-TR")}</strong> {c.unit} · {c.delivery_count || 0} teslim
                </div>
              </div>

              {c.unit_price > 0 && !(Array.isArray(c.items) && c.items.length > 1) && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Birim Fiyat{c.vat_rate ? ` (KDV %${c.vat_rate})` : ""}</span>
                  <span className="font-mono text-slate-800">₺{c.unit_price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                </div>
              )}

              {(c.payment_terms || c.payment_term_days > 0) && (
                <div className="mt-2 flex items-start justify-between text-xs gap-2">
                  <span className="text-slate-500 shrink-0">Ödeme Koşulu</span>
                  <span className="text-slate-700 text-right">
                    {c.payment_terms || `${c.payment_term_days} gün vade`}
                    {c.payment_terms && c.payment_term_days > 0 && (
                      <span className="ml-1 text-slate-400 font-mono">({c.payment_term_days} gün)</span>
                    )}
                  </span>
                </div>
              )}

              {c.shipping_included === false && c.shipping_unit_price > 0 && (
                <div className="mt-2 flex items-start justify-between text-xs gap-2">
                  <span className="text-slate-500 shrink-0">Nakliye</span>
                  <span className="text-slate-700 text-right font-mono">
                    ₺{(c.shipping_unit_price || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} × {c.shipping_quantity || 0} {c.shipping_unit || ""}
                  </span>
                </div>
              )}
              {c.shipping_included === true && c.source_rfq_id && (
                <div className="mt-2 text-[10px] text-slate-400 italic">Nakliye fiyata dahil</div>
              )}

              {c.source_rfq_code && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-500 flex items-center gap-1">
                  <FileText size={11} className="text-slate-400" />
                  Kaynak: <span className="font-mono font-bold text-slate-600">{c.source_rfq_code}</span>
                  {c.source_rfq_revision > 0 && (
                    <span className="font-mono text-slate-400">Rev.{String(c.source_rfq_revision).padStart(2, "0")}</span>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {rows.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500 flex flex-col items-center gap-2">
            <FileText size={32} weight="duotone" className="text-slate-400" />
            <div>Henüz sözleşme yok.</div>
          </div>
        )}
      </div>

      {/* Hızlı Firma Kaydı Sheet'i (dialog içindeki "Yeni Firma" butonundan açılır) */}
      <SupplierQuickCreate
        open={supplierSheetOpen}
        onOpenChange={setSupplierSheetOpen}
        pendingInfo={{}}
        pendingRole="issuer"
        saving={supplierSaving}
        saveFn={saveSupplierFromDialog}
      />
    </Shell>
  );
}
